// Daily Stock Activity Tracker
// Tracks which days each product/location combination had stock (quantity > 0)
// Used for accurate average daily sales and coverage calculations

import { getStockLocations, getStockDataForProduct } from './unifiedDataHelpers';
import { getCurrentDate as getCurrentDateFromUtils, parseDate } from './dateUtils';
import { kvGetRaw, kvSetRaw, listKvKeys } from '../infrastructure/storage/kvAccess';

const STORAGE_KEY_PREFIX = 'daily_stock_activity_';
const STORAGE_METADATA_KEY = 'daily_stock_activity_metadata';

// Get current date in Mauritanian time (GMT+0)
export const getCurrentDate = () => {
  return getCurrentDateFromUtils();
};

// Get date from a timestamp in Mauritanian time (GMT+0)
export const getDateFromTimestamp = (timestamp) => {
  return parseDate(timestamp);
};

// Get date 30 days ago in Mauritanian time (GMT+0)
export const getDate30DaysAgo = () => {
  const date = new Date();
  date.setDate(date.getDate() - 30);
  return date.toISOString().split('T')[0];
};

// Record stock activity for a specific product and location
export const recordStockActivity = (productId, location, quantity, timestamp = null) => {
  try {
    const date = timestamp ? getDateFromTimestamp(timestamp) : getCurrentDate();
    const key = `${STORAGE_KEY_PREFIX}${productId}_${location}`;
    
    // Get existing activity data
    let activityData = kvGetRaw(key);
    let dailyActivity = {};
    
    if (activityData) {
      try {
        dailyActivity = JSON.parse(activityData);
      } catch (error) {
        dailyActivity = {};
      }
    }
    
    // Record this day's activity
    dailyActivity[date] = {
      quantity: Number(quantity) || 0,
      hasStock: Number(quantity) > 0,
      lastUpdated: new Date().toISOString()
    };
    
    // Clean up old data (keep only last 60 days)
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - 60);
    const cutoffDateStr = cutoffDate.toISOString().split('T')[0];
    
    Object.keys(dailyActivity).forEach(activityDate => {
      if (activityDate < cutoffDateStr) {
        delete dailyActivity[activityDate];
      }
    });
    
    // Save updated data
    kvSetRaw(key, JSON.stringify(dailyActivity));
    
    // Update metadata
    updateActivityMetadata(productId, location, date);
    
    return true;
  } catch (error) {
    // Error recording stock activity
    return false;
  }
};

// Get active days count for a product and location in the last N days
export const getActiveDaysCount = (productId, location, days = 30) => {
  try {
    const key = `${STORAGE_KEY_PREFIX}${productId}_${location}`;
    const activityData = kvGetRaw(key);
    
    if (!activityData) return 0;
    
    let dailyActivity;
    try {
      dailyActivity = JSON.parse(activityData);
    } catch (error) {
      return 0;
    }
    
    // Sort dates in descending order (newest first)
    const sortedDates = Object.keys(dailyActivity).sort().reverse();
    
    let activeDaysCount = 0;
    let daysChecked = 0;
    
    // Go through dates from newest to oldest until we find enough active days
    for (const date of sortedDates) {
      daysChecked++;
      if (dailyActivity[date].hasStock) {
        activeDaysCount++;
      }
      
      // Stop when we have enough active days OR we've checked too many days
      if (activeDaysCount >= days || daysChecked >= days * 3) { // Max 3x days to prevent infinite loop
        break;
      }
    }
    
    return activeDaysCount;
  } catch (error) {
    // Error getting active days count
    return 0;
  }
};

// Get the most recent N active days for a product and location
export const getActiveDates = (productId, location, days = 30) => {
  try {
    const key = `${STORAGE_KEY_PREFIX}${productId}_${location}`;
    const activityData = kvGetRaw(key);
    
    if (!activityData) return [];
    
    let dailyActivity;
    try {
      dailyActivity = JSON.parse(activityData);
    } catch (error) {
      return [];
    }
    
    // Sort dates in descending order (newest first)
    const sortedDates = Object.keys(dailyActivity).sort().reverse();
    
    const activeDates = [];
    let daysChecked = 0;
    
    // Go through dates from newest to oldest until we find enough active days
    for (const date of sortedDates) {
      daysChecked++;
      if (dailyActivity[date].hasStock) {
        activeDates.push(date);
      }
      
      // Stop when we have enough active days OR we've checked too many days
      if (activeDates.length >= days || daysChecked >= days * 3) { // Max 3x days to prevent infinite loop
        break;
      }
    }
    
    return activeDates.sort(); // Return in chronological order
  } catch (error) {
    // Error getting active dates
    return [];
  }
};

// Get sales data from the most recent 30 active days for accurate average calculation
export const getSalesFromActiveDays = (productId, location, sales, days = 30) => {
  try {
    // Get the 30 most recent active days
    const activeDates = getActiveDates(productId, location, days);
    
    if (activeDates.length === 0) return { totalSales: 0, activeDaysCount: 0 };
    
    let totalSales = 0;
    
    // Calculate sales from the active days
    sales.forEach(sale => {
      const saleDate = new Date(sale.date).toISOString().split('T')[0];
      
      // Check if this sale occurred on one of the active days
      if (activeDates.includes(saleDate)) {
        const saleProducts = sale.products || [{ 
          productId: sale.productId, 
          quantity: sale.quantity,
          stockLocation: sale.stockLocation || 'Principal'
        }];
        
        saleProducts.forEach(sp => {
          if (String(sp.productId) === String(productId)) {
            // Only count sales from the specified stock location
            const stockLocation = sp.stockLocation || sale.stockLocation || 'Principal';
            if (stockLocation === location) {
              totalSales += Number(sp.quantity || 0);
            }
          }
        });
      }
    });
    
    return {
      totalSales,
      activeDaysCount: activeDates.length
    };
  } catch (error) {
    // Error getting sales from active days
    return { totalSales: 0, activeDaysCount: 0 };
  }
};

// NEW: Hybrid approach for average daily sales calculation
// Handles both startup period (< 30 active days) and mature period (≥ 30 active days)
export const getHybridAverageDailySales = (productId, location, sales) => {
  try {
    // Get all available active days (not limited to 30)
    const allActiveDates = getActiveDates(productId, location, 999); // Get all available
    const totalActiveDays = allActiveDates.length;
    
    // HYBRID CALC Product
    
    // FALLBACK: If no active days data, calculate from sales data directly
    // This should rarely happen if sales exist, as sales should create active days
    if (totalActiveDays === 0) {
      // No active days data for product, checking sales data
      
      // Calculate total sales and actual active days for this product
      let totalSales = 0;
      const activeSaleDates = new Set();
      
      sales.forEach(sale => {
        const saleDate = new Date(sale.date).toISOString().split('T')[0];
        const saleProducts = sale.products || [{ 
          productId: sale.productId, 
          quantity: sale.quantity,
          stockLocation: sale.stockLocation || 'Principal'
        }];
        
        saleProducts.forEach(sp => {
          if (String(sp.productId) === String(productId)) {
            const stockLocation = sp.stockLocation || sale.stockLocation || 'Principal';
            if (stockLocation === location) {
              totalSales += Number(sp.quantity || 0);
              activeSaleDates.add(saleDate);
            }
          }
        });
      });
      
      const actualActiveDays = activeSaleDates.size;
      
      // If we have sales but no active days, this indicates a data inconsistency
      if (actualActiveDays === 0 && totalSales > 0) {
        // Data inconsistency: Product has sales but no active days recorded
        // Return 0 to avoid division by zero
        return { 
          avgDailySales: 0, 
          totalSales: 0, 
          activeDaysCount: 0, 
          isStartupPeriod: true,
          calculationMethod: 'Data inconsistency: sales exist but no active days'
        };
      }
      
      // Calculate average based on actual active days
      const avgDailySales = actualActiveDays > 0 ? totalSales / actualActiveDays : 0;
      
      // HYBRID CALC FALLBACK
      
      return { 
        avgDailySales, 
        totalSales, 
        activeDaysCount: actualActiveDays, 
        isStartupPeriod: actualActiveDays < 30,
        calculationMethod: `Fallback: ${totalSales} ÷ ${actualActiveDays} = ${avgDailySales.toFixed(2)}`
      };
    }
    
    let totalSales = 0;
    let recent30Sales = 0; // Declare at function level to fix scope issue
    
    // Calculate total sales from all active days
    sales.forEach(sale => {
      const saleDate = new Date(sale.date).toISOString().split('T')[0];
      
      if (allActiveDates.includes(saleDate)) {
        const saleProducts = sale.products || [{ 
          productId: sale.productId, 
          quantity: sale.quantity,
          stockLocation: sale.stockLocation || 'Principal'
        }];
        
        saleProducts.forEach(sp => {
          if (String(sp.productId) === String(productId)) {
            const stockLocation = sp.stockLocation || sale.stockLocation || 'Principal';
            if (stockLocation === location) {
              totalSales += Number(sp.quantity || 0);
            }
          }
        });
      }
    });
    
    // HYBRID APPROACH:
    // Phase 1: Startup Period (< 30 active days) - Use actual active days
    // Phase 2: Mature Period (≥ 30 active days) - Use 30-day rolling window
    
    let avgDailySales;
    let isStartupPeriod;
    
    if (totalActiveDays < 30) {
      // STARTUP PERIOD: Divide by actual active days for realistic averages
      avgDailySales = totalSales / totalActiveDays;
      isStartupPeriod = true;
      
      // HYBRID CALC STARTUP
    } else {
      // MATURE PERIOD: Use 30-day rolling window for consistency
      const recent30ActiveDates = getActiveDates(productId, location, 30);
      let recent30Sales = 0; // Reset for mature period calculation
      
      sales.forEach(sale => {
        const saleDate = new Date(sale.date).toISOString().split('T')[0];
        if (recent30ActiveDates.includes(saleDate)) {
          const saleProducts = sale.products || [{ 
            productId: sale.productId, 
            quantity: sale.quantity,
            stockLocation: sale.stockLocation || 'Principal'
          }];
          
          saleProducts.forEach(sp => {
            if (String(sp.productId) === String(productId)) {
              const stockLocation = sp.stockLocation || sale.stockLocation || 'Principal';
              if (stockLocation === location) {
                recent30Sales += Number(sp.quantity || 0);
              }
            }
          });
        }
      });
      
      avgDailySales = recent30Sales / 30; // Always divide by 30 in mature period
      isStartupPeriod = false;
      
      // HYBRID CALC MATURE
    }
    
    return {
      avgDailySales,
      totalSales,
      activeDaysCount: totalActiveDays,
      isStartupPeriod,
      calculationMethod: isStartupPeriod ? 
        `Startup: ${totalSales} ÷ ${totalActiveDays} = ${avgDailySales.toFixed(2)}` :
        `Mature: ${recent30Sales} ÷ 30 = ${avgDailySales.toFixed(2)}`
    };
  } catch (error) {
    // Error getting hybrid average daily sales
    return { avgDailySales: 0, totalSales: 0, activeDaysCount: 0, isStartupPeriod: true };
  }
};

/**
 * ONE-PASS hybrid velocity for all products at a location.
 * Avoids O(products × sales) when building Mouvement de stock.
 * Derives active days from sales dates (no per-product KV reads).
 *
 * @param {Array} sales
 * @param {string} [location='Principal']
 * @returns {Map<string, { avgDailySales: number, totalSales: number, activeDaysCount: number, isStartupPeriod: boolean, calculationMethod: string }>}
 */
export const buildHybridAverageDailySalesByProduct = (sales, location = 'Principal') => {
  const byProductDays = new Map(); // productId -> Map(ymd -> qty)

  const list = Array.isArray(sales) ? sales : [];
  for (let i = 0; i < list.length; i += 1) {
    const sale = list[i];
    if (!sale) continue;
    let ymd;
    try {
      ymd = new Date(sale.date).toISOString().split('T')[0];
    } catch {
      continue;
    }
    if (!ymd || ymd === 'Invalid Date') continue;

    const saleProducts = sale.products || [{
      productId: sale.productId,
      quantity: sale.quantity,
      stockLocation: sale.stockLocation || 'Principal'
    }];

    for (let j = 0; j < saleProducts.length; j += 1) {
      const sp = saleProducts[j];
      if (sp?.productId == null) continue;
      const stockLocation = sp.stockLocation || sale.stockLocation || 'Principal';
      if (stockLocation !== location) continue;
      const qty = Number(sp.quantity || 0) || 0;
      if (qty <= 0) continue;

      const pid = String(sp.productId);
      let dayMap = byProductDays.get(pid);
      if (!dayMap) {
        dayMap = new Map();
        byProductDays.set(pid, dayMap);
      }
      dayMap.set(ymd, (dayMap.get(ymd) || 0) + qty);
    }
  }

  const result = new Map();
  for (const [productId, dayMap] of byProductDays.entries()) {
    const dates = Array.from(dayMap.keys()).sort();
    const totalActiveDays = dates.length;
    let totalSalesQty = 0;
    for (let i = 0; i < dates.length; i += 1) {
      totalSalesQty += dayMap.get(dates[i]) || 0;
    }

    let avgDailySales = 0;
    let isStartupPeriod = true;
    let calculationMethod = 'No sales';

    if (totalActiveDays > 0) {
      if (totalActiveDays < 30) {
        avgDailySales = totalSalesQty / totalActiveDays;
        isStartupPeriod = true;
        calculationMethod = `Startup: ${totalSalesQty} ÷ ${totalActiveDays} = ${avgDailySales.toFixed(2)}`;
      } else {
        const recent30 = dates.slice(-30);
        let recent30Sales = 0;
        for (let i = 0; i < recent30.length; i += 1) {
          recent30Sales += dayMap.get(recent30[i]) || 0;
        }
        avgDailySales = recent30Sales / 30;
        isStartupPeriod = false;
        calculationMethod = `Mature: ${recent30Sales} ÷ 30 = ${avgDailySales.toFixed(2)}`;
      }
    }

    result.set(productId, {
      avgDailySales,
      totalSales: totalSalesQty,
      activeDaysCount: totalActiveDays,
      isStartupPeriod,
      calculationMethod
    });
  }

  return result;
};

// Update activity metadata for tracking purposes
const updateActivityMetadata = (productId, location, date) => {
  try {
    let metadata = kvGetRaw(STORAGE_METADATA_KEY);
    let metadataObj = {};
    
    if (metadata) {
      try {
        metadataObj = JSON.parse(metadata);
      } catch (error) {
        metadataObj = {};
      }
    }
    
    if (!metadataObj[productId]) {
      metadataObj[productId] = {};
    }
    
    if (!metadataObj[productId][location]) {
      metadataObj[productId][location] = {};
    }
    
    metadataObj[productId][location].lastActivity = date;
    metadataObj[productId][location].lastUpdated = new Date().toISOString();
    
    kvSetRaw(STORAGE_METADATA_KEY, JSON.stringify(metadataObj));
  } catch (error) {
    // Error updating activity metadata
  }
};

// Initialize daily tracking for existing products (called on app startup)
export const initializeDailyTracking = (products) => {
  try {
    const currentDate = getCurrentDate();
    
    products.forEach(product => {
      // Track Principal location using unified structure
      const stockData = getStockDataForProduct(product.id, 'Principal');
      const currentStock = stockData ? (stockData.stockNow || 0) : 0;
      
      if (currentStock > 0) {
        recordStockActivity(product.id, 'Principal', currentStock);
      }
      
      // Track additional stock locations
      try {
        const locations = getStockLocations();
        locations.forEach(location => {
          if (location !== 'Principal') {
            const stockData = getStockDataForProduct(product.id, location);
            if (stockData) {
              const stockNow = Number(stockData.stockNow) || 0;
              if (stockNow > 0) {
                recordStockActivity(product.id, location, stockNow);
              }
            }
          }
        });
      } catch (error) {
        // Error initializing additional stock tracking
      }
    });
  } catch (error) {
    // Error initializing daily tracking
  }
};

// Clean up old activity data (called periodically)
export const cleanupOldActivityData = () => {
  try {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - 90); // Keep only last 90 days
    const cutoffDateStr = cutoffDate.toISOString().split('T')[0];
    
    // Get all keys with our prefix
    const keysToCheck = listKvKeys().filter((key) => key && key.startsWith(STORAGE_KEY_PREFIX));

    keysToCheck.forEach((key) => {
      try {
        const activityData = kvGetRaw(key);
        if (activityData) {
          let dailyActivity = JSON.parse(activityData);
          let hasChanges = false;
          
          Object.keys(dailyActivity).forEach(activityDate => {
            if (activityDate < cutoffDateStr) {
              delete dailyActivity[activityDate];
              hasChanges = true;
            }
          });
          
          if (hasChanges) {
            kvSetRaw(key, JSON.stringify(dailyActivity));
          }
        }
      } catch (error) {
        // Error cleaning up activity data for key
      }
    });
  } catch (error) {
    // Error cleaning up old activity data
  }
};
